import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { MatSelectionList, MatSelectionListChange } from "@angular/material/list";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";

import { BehaviorSubject, EMPTY, Observable, of, Subscription } from "rxjs";
import { catchError, finalize, map, take, tap } from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import {
  DefaultConfirmationDialogData,
  Dialog
} from "src/app/models/dialog/dialog.model";
import { FileOperationStatus, PlaylistDTO } from "src/app/models/playlists/playlistDTO";
import { MultimediaFileDTO } from "src/app/models/playlists/multimediaFileDTO";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { ManageFileService } from "src/app/shared/services/manage-files/manage-files.service";

@Component({
  selector: 'app-manage-files-dialog',
  templateUrl: './manage-files-dialog.component.html',
  styleUrls: ['./manage-files-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ManageFilesDialogComponent implements OnInit {
    //Playlist
    isLoading$ = new BehaviorSubject<boolean>(false);
    loadedPlaylist$: Observable<PlaylistDTO>;
    created$: Observable<boolean>;
  
    fileList$: Observable<MultimediaFileDTO[]>;
    fileList: MultimediaFileDTO[];
    selectedFiles: string[];
    searchTooltipText: string;
    searchTerms: string[] = [];
  
    //Thumbnail
    private thumbnailCache: { [fileId: string]: SafeUrl } = {};
    public fileThumbnails: { [fileId: string]: Observable<SafeUrl> } = {}; 
  
    selectedUploadFile: File;
    allowMultiple: boolean;
    fileType: string;

    fileOperationStatus = FileOperationStatus;

    idsSelected: any

    @ViewChild('selectionList') selectionList: MatSelectionList;

  constructor(
    private manageFileService: ManageFileService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<ManageFilesDialogComponent>
  ) { }

  ngOnInit(): void {
    this.loadFiles();
    this.idsSelected = this.data.treatedDtoSelection
    this.allowMultiple = this.data.allowMultiple ?? true
    this.fileType = this.data.fileType
  }

  get fileHintKey(): string {
    if (this.fileType?.includes('video')) {
      return 'Playlist.fileMaxSize';
    } else if (this.fileType?.includes('image')) {
      return 'Playlist.fileMaxSizeImage';
    }
  }

loadFiles(filteringArgs?: any): void {
  this.isLoading$.next(true);

  this.fileList$ = this.manageFileService.getFilesList(filteringArgs).pipe(
    map(({ items }) => {
      const allowedExtensions = this.getAllowedExtensions();
      this.fileList = items.filter(file =>
        allowedExtensions.some(ext => file.name?.toLowerCase().endsWith(ext))
      );
      return this.fileList;
    }),
    catchError((error) => {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.filesLoadError"),
        type: SnackbarType.ERROR,
      });
      return of([]);
    }),
    finalize(() => {
      this.isLoading$.next(false);
    })
  );
}

  private getAllowedExtensions(): string[] {
    if (!this.fileType) return [];
  
    const type = this.fileType?.toLowerCase();
  
    if (type.includes('image')) {
      return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    } else if (type.includes('video')) {
      return ['.mp4', '.avi', '.mov', '.mkv', '.webm'];
    } else {
      return [];
    }
  }

  selectUploadFile(event: any) {
    this.selectedUploadFile = event.target.files[0];
  }

  saveUploadFile(file: File) {
    if (!file) return;

    const allowedExtensions = this.getAllowedExtensions();
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  
    if (!allowedExtensions.includes(extension)) {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.fileWrongType"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    const { size } = file;
    if (size > 209715200) {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.fileMaxSizeExceeded"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    this.isLoading$.next(true);
    this.manageFileService
      .createFile(file)
      .pipe(
        tap(() => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileUploaded"),
            type: SnackbarType.SUCCESS,
          });
          this.loadFiles();
          this.selectedUploadFile = undefined;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileUploadFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  downloadFile(file: any): void {
    if (!file || !file.id) {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.fileInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
  
    this.manageFileService.downloadThumbnailFile(file.id).subscribe(
      (blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
  
        link.href = blobUrl;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
  
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      },
      (error) => {
        this.snackbarService.show({
          message: this.translate.instant("Playlist.fileDeleteFail"),
          type: SnackbarType.ERROR,
        });
        return EMPTY;
      }
    );
  }

  openFileDeleteDialog(file: MultimediaFileDTO) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteFile(file.id);
      }
    });
  }

  deleteFile(id: string) {
    this.isLoading$.next(true);
    this.manageFileService
      .deleteFile(id)
      .pipe(
        tap(() => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileDeleted"),
            type: SnackbarType.SUCCESS,
          });
          this.loadFiles();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileDeleteFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  getThumbnail(fileId: string): Observable<SafeUrl> {
    if (this.thumbnailCache[fileId]) {
      return of(this.thumbnailCache[fileId]);
    }

    if (!this.fileThumbnails[fileId]) {
      this.fileThumbnails[fileId] = this.manageFileService.getFileThumbnail(fileId).pipe(
        map(blob => {
          const url = URL.createObjectURL(blob);
          const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
          this.thumbnailCache[fileId] = safeUrl;
          return safeUrl;
        })
      );
    }

    return this.fileThumbnails[fileId];
  }

  invalidFileStatus(files: MultimediaFileDTO[]): boolean {
    let invalid = false;
    if (!this.selectedFiles ||  !this.selectedFiles.length) {
      return invalid;
    }
    this.selectedFiles.forEach((selFileId) => {
      if (files.find(({ id }) => id === selFileId).fileStatus !== FileOperationStatus.CONVERSION_DONE) {
        invalid = true;
      };
    });
    return invalid;
  }

  onSelectionChange(event: MatSelectionListChange) {
    const [option] = event.options;
    const selectedId = option.value.id;
  
    if (this.allowMultiple) {
      const alreadySelected = this.idsSelected?.includes(selectedId);
  
      if (alreadySelected) {
        this.idsSelected = this.idsSelected.filter((id) => id !== selectedId);
      } else {
        this.idsSelected = this.idsSelected ? [...this.idsSelected, selectedId] : [selectedId];
      }
    } else {
      this.idsSelected = [selectedId];
    }
  }

  checkSelected(file: any) {
    const { id } = file;

    if (!this.idsSelected || !id) {
      return;
    }

    return this.idsSelected.some((fileId) => fileId === id);
  }

  isPreselected(file: any): boolean {
    return this.idsSelected?.includes(file.id);
  }

  isDisabledFile(file: any): boolean {
    const isOriginallySelected = this.idsSelected?.includes(file.id);
    const isCurrentlySelected = this.selectionList?.selectedOptions?.selected
      .map(opt => opt.value.id)
      .includes(file.id);
  
    return isOriginallySelected && !isCurrentlySelected;
  }

  selectAll() {
    this.idsSelected = this.fileList.map(({ id }) => id);
  }

  unselectAll() {
    this.idsSelected = [];
  }

  public onConfirmClick(): void {
    const selectedFileObjects = this.fileList
      .filter(file => this.idsSelected.includes(file.id))
      .map(file => ({ id: file.id, name: file.name }));
  
    this.dialogRef.close(selectedFileObjects);
  }

  public onCancelClick(): void {
    this.dialogRef.close(true)
  }

}
